import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Step back up the flow.
 *
 * Navigating to the parent path instead would push a new entry, so the stack
 * grows — go to a category, come back, and the browser's own back button
 * returns to the category rather than leaving the menu. Stepping back keeps
 * the stack honest.
 *
 * React Router records the entry index in history.state, so we can tell a
 * normal visit from a deep link (idx 0), where there is nothing behind us and
 * navigating back would leave the app. There we replace with the parent path.
 */
export const useGoBack = (fallbackPath?: string) => {
  const navigate = useNavigate();

  return useCallback(() => {
    const idx = (window.history.state as { idx?: number } | null)?.idx;

    if (typeof idx === "number" && idx > 0) {
      navigate(-1);
      return;
    }

    if (fallbackPath) {
      navigate(fallbackPath, { replace: true });
    }
  }, [navigate, fallbackPath]);
};
